import { queryOptions, useQuery } from '@tanstack/react-query';
import { TrainRecordingService } from '../generated';
import { ApiError } from '../../components/ApiLoadingGuard/ApiLoadingGuard';
import { blobToDataURL } from '../common';

export const trainAuxImageKeys = {
  base: () => ['trainAuxImage'] as const,
  one: ({ jobsiteId, recordingId, auxIndex, imageIndex }: TrainAuxImageOptionsProps) =>
    [...trainAuxImageKeys.base(), jobsiteId, recordingId, auxIndex, imageIndex] as const,
};

interface TrainAuxImageOptionsProps {
  jobsiteId: string;
  recordingId: string;
  auxIndex: number;
  imageIndex: number;
}

function trainAuxImageOptions({ jobsiteId, recordingId, auxIndex, imageIndex }: TrainAuxImageOptionsProps) {
  return queryOptions<string, ApiError>({
    queryKey: trainAuxImageKeys.one({ jobsiteId, recordingId, auxIndex, imageIndex }),
    queryFn: async () => {
      if (!jobsiteId || !recordingId) {
        throw new Error('jobsiteId, recordingId, auxIndex and imageIndex are required');
      }

      const response = await TrainRecordingService.trainRecordingControllerGetAuxImage({
        path: { jobsiteId, recordingId, numAux: auxIndex.toString(), numImage: imageIndex },
      });

      if (response.data !== undefined) {
        return await blobToDataURL(response.data as Blob);
      } else {
        throw (response as any).error || new Error(response.response?.statusText || 'Unknown error');
      }
    },
    enabled: !!jobsiteId && !!recordingId,
  });
}

export function useTrainAuxImage(props: TrainAuxImageOptionsProps) {
  return useQuery(trainAuxImageOptions(props));
}

export const trainAuxImageCountKeys = {
  base: () => ['trainAuxImageCount'] as const,
  one: ({ jobsiteId, recordingId, auxIndex }: TrainAuxImageCountOptionsProps) =>
    [...trainAuxImageCountKeys.base(), jobsiteId, recordingId, auxIndex] as const,
};

interface TrainAuxImageCountOptionsProps {
  jobsiteId: string;
  recordingId: string;
  auxIndex: string;
}

function trainAuxImageCountOptions({ jobsiteId, recordingId, auxIndex }: TrainAuxImageCountOptionsProps) {
  return queryOptions<number, ApiError>({
    queryKey: trainAuxImageCountKeys.one({ jobsiteId, recordingId, auxIndex }),
    queryFn: async () => {
      if (!jobsiteId || !recordingId || !auxIndex) {
        throw new Error('jobsiteId, recordingId and auxIndex are required');
      }

      const response = await TrainRecordingService.trainRecordingControllerGetAuxImages({
        path: { jobsiteId, recordingId, numAux: auxIndex },
      });

      if (response.data !== undefined) {
        return response.data.numImages;
      } else {
        throw (response as any).error || new Error(response.response?.statusText || 'Unknown error');
      }
    },
    enabled: !!jobsiteId && !!recordingId,
  });
}

export function useTrainAuxImageCount(props: TrainAuxImageCountOptionsProps) {
  return useQuery(trainAuxImageCountOptions(props));
}

export const trainAuxCapturesKeys = {
  base: () => ['trainAuxCaptures'] as const,
  one: ({ jobsiteId, recordingId }: TrainAuxCapturesOptionsProps) =>
    [...trainAuxImageCountKeys.base(), jobsiteId, recordingId] as const,
};

export interface AuxCaptureInfo {
  auxIndex: number;
  label: string;
}

export interface TrainAuxCapturesData {
  auxCaptures: AuxCaptureInfo[];
}

interface TrainAuxCapturesOptionsProps {
  jobsiteId: string | undefined;
  recordingId: string;
}

export function trainAuxCapturesOptions({ jobsiteId, recordingId }: TrainAuxCapturesOptionsProps) {
  return queryOptions<TrainAuxCapturesData, ApiError>({
    queryKey: trainAuxCapturesKeys.one({ jobsiteId, recordingId }),
    queryFn: async () => {
      if (!jobsiteId || !recordingId) {
        throw new Error('jobsiteId and recordingId are required');
      }
      const response = await TrainRecordingService.trainRecordingControllerGetAuxCaptures({
        path: { jobsiteId, recordingId },
      });

      if (response.data !== undefined) {
        const auxCaptures: AuxCaptureInfo[] = response.data.auxIndices.map(toAuxCaptureInfo);
        return { auxCaptures: auxCaptures };
      } else {
        throw (response as any).error || new Error(response.response?.statusText || 'Unknown error');
      }
    },
    enabled: !!jobsiteId && !!recordingId,
  });
}

export function useTrainAuxCaptures(props: TrainAuxCapturesOptionsProps) {
  return useQuery(trainAuxCapturesOptions(props));
}

// Helper functions
function toAuxCaptureInfo(auxIndexStr: string): AuxCaptureInfo {
  const auxIndex = parseInt(auxIndexStr, 10);

  return {
    auxIndex,
    label: getAuxLabel(auxIndex),
  };
}

const getAuxLabel = (auxIndex: number) => {
  if (auxIndex === 1) {
    return 'recording.auxiliary.right';
  } else if (auxIndex === 0) {
    return 'recording.auxiliary.left';
  }
  return 'recording.auxiliary.unlabeled';
};
